import { useMemo } from "react";
import { ArrowRightLeft, Check, X } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { RESTAURANT_BY_ID } from "../../constants";
import { canApprove } from "../../lib/permissions";
import { timeAgo } from "../../lib/storage";

function TransferItem({ item, product }) {
  return (
    <div className="flex items-center gap-3 bg-[#FBF8F1] border border-[#EEE8DC] rounded-lg p-2">
      {product?.image ? (
        <img
          src={product.image}
          alt=""
          className="h-10 w-10 rounded object-cover shrink-0"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="h-10 w-10 rounded bg-[#F3EFE7] shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{product?.name || "—"}</p>
        <p className="text-xs text-[#0B1F3A]/60">
          {item.quantity ?? item.qty} {item.unit ?? product?.unit}
        </p>
      </div>
    </div>
  );
}

function TransferCard({ transfer, user, productById, onApprove, onReject, onFulfill }) {
  const fromR = RESTAURANT_BY_ID[transfer.fromRestaurant];
  const toR = RESTAURANT_BY_ID[transfer.toRestaurant];
  const pending = transfer.status === "submitted" || transfer.status === "pending";
  return (
    <div className="bg-white border border-[#E6E0D4] rounded-xl p-4 md:p-5" data-testid={`transfer-${transfer.id}`}>
      <div className="flex items-start gap-3 md:gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-[#E6E9F4] text-[#0B1F3A] flex items-center justify-center shrink-0">
          <ArrowRightLeft size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-medium">Internal transfer</p>
            <Badge variant="outline" className="border-[#0B1F3A]/30 text-[#0B1F3A]/80 bg-[#F3EFE7]">
              {fromR?.name} → {toR?.name}
            </Badge>
            <Badge
              variant="outline"
              className={`${
                transfer.status === "approved" || transfer.status === "fulfilled"
                  ? "border-[#2B7A6B]/40 text-[#2B7A6B] bg-[#E3F0EC]"
                  : transfer.status === "rejected"
                  ? "border-red-300 text-red-600 bg-red-50"
                  : "border-[#E6E0D4] text-[#0B1F3A]/70 bg-[#F3EFE7]"
              }`}
            >
              {transfer.status}
            </Badge>
          </div>
          <p className="text-xs text-[#0B1F3A]/60 mt-0.5">
            {transfer.items?.length} SKU{(transfer.items?.length || 0) > 1 ? "s" : ""} · {transfer.requestedByName} ·{" "}
            {timeAgo(transfer.createdAt)}
          </p>
        </div>
        {pending && canApprove(user, transfer) && (
          <div className="flex gap-2 ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReject(transfer)}
              className="border-[#E6E0D4]"
              data-testid={`transfer-reject-${transfer.id}`}
            >
              <X size={14} className="mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={() => onApprove(transfer)}
              className="bg-[#0B1F3A] hover:bg-[#11294A] text-white"
              data-testid={`transfer-approve-${transfer.id}`}
            >
              <Check size={14} className="mr-1" /> Approve
            </Button>
          </div>
        )}
        {transfer.status === "approved" && canApprove(user, transfer) && (
          <Button
            size="sm"
            onClick={() => onFulfill(transfer)}
            className="bg-[#2B7A6B] hover:bg-[#256a5c] text-white ml-auto"
            data-testid={`transfer-complete-${transfer.id}`}
          >
            <Check size={14} className="mr-1" /> Mark completed
          </Button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {transfer.items?.map((it) => (
          <TransferItem key={`${transfer.id}-${it.productId}`} item={it} product={productById(it.productId)} />
        ))}
      </div>
      {transfer.notes && <p className="mt-3 text-xs text-[#0B1F3A]/65 italic">“{transfer.notes}”</p>}
    </div>
  );
}

export default function TransferList({ transfers, products, user, onApprove, onReject, onFulfill }) {
  const productById = useMemo(() => {
    const map = new Map(products.map((p) => [p.id, p]));
    return (id) => map.get(id);
  }, [products]);
  if (transfers.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="transfer-list">
      {transfers.map((r) => (
        <TransferCard
          key={r.id}
          transfer={r}
          user={user}
          productById={productById}
          onApprove={onApprove}
          onReject={onReject}
          onFulfill={onFulfill}
        />
      ))}
    </div>
  );
}
